import {Tag} from 'platform/components';
import {HStack, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useSelector} from 'react-redux';

import {isNil, isNotNil, slice} from 'ramda';

import {
  selectTenant,
  useGetAuctionServerListQuery,
  useSourcingGetSourcingServersQuery,
} from '@dms/api/core';
import {selectCurrentUserInfo} from '@dms/api/customer';
import {i18n} from '@dms/i18n';
import {SkeletonLoadingBox, useAddress} from '@dms/shared';

import {suffixTestId, TestIdProps, useToggle} from 'shared/web';

import {KwToPsMultiplier} from '../../../../constants/KwToPsMultiplier';
import {sourcingFilterConstants} from '../../../../constants/sourcingFilterConstants';
import {useApiEnums} from '../../../../hooks/useApiEnums';
import {useMakeModelFilter} from '../../hooks/useMakeModelFilter';
import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {AuctionTypeFilter} from '../../types/AuctionTypeFilter';
import {CarStatusFilter} from '../../types/CarStatusFilter';
import {useFilters} from '../FiltersContext/FiltersContext';
import {FilterTagButtons} from './components/FilterTagButtons';
import {useGetBooleanBasedTags} from './hooks/useGetBooleanBasedTags';
import {useGetNumberBasedTags} from './hooks/useGetNumberBasedTags';
import {useGetStringBasedTags} from './hooks/useGetStringBasedTags';

const NUMBER_OF_VISIBLE_TAGS = 8;

interface FilterTagsProps extends TestIdProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  showSaveButton?: boolean;
}

export function FilterTags(props: FilterTagsProps) {
  const [isExpanded, toggleIsExpanded] = useToggle(false);
  const {filters, onUpdateFilters} = useFilters();
  const {
    selectedMakes,
    selectedModelFamilies,
    selectedModels,
    getMake,
    getModel,
    getModelFamily,
    unselectMake,
    unselectModel,
    unselectModelFamily,
    isLoading: areMakeModelsLoading,
  } = useMakeModelFilter();
  const {data: {country: countryCode} = {}} = useSelector(selectTenant);
  const {data: sellerServers} = useSourcingGetSourcingServersQuery(
    {countryCode: countryCode!},
    {skip: isNil(countryCode)}
  );
  const {data: auctionServers} = useGetAuctionServerListQuery();
  const selectedCurrency = useSelector(selectCurrentUserInfo)?.data?.settings.currency;
  const currencySuffix = isNotNil(selectedCurrency) ? ` ${selectedCurrency}` : '';
  const {composeAddress} = useAddress();
  const {getEnum, isLoading: areEnumsLoading} = useApiEnums({flat: true});

  const getStringBasedTags = useGetStringBasedTags();
  const getNumberBasedTags = useGetNumberBasedTags();
  const getBooleanBasedTags = useGetBooleanBasedTags();

  const sellerServerTags =
    filters.sellerServer?.map((sellerServer) => ({
      label: sellerServers?.find(({code}) => code === sellerServer)?.name ?? sellerServer,
      onDelete: () =>
        onUpdateFilters(
          'sellerServer',
          filters.sellerServer?.filter((item) => sellerServer !== item)
        ),
    })) ?? [];

  const auctionServerTags =
    filters.auctionServer?.map((auctionServerId) => ({
      label: auctionServers?.find(({id}) => id === auctionServerId)?.name ?? auctionServerId,
      onDelete: () =>
        onUpdateFilters(
          'auctionServer',
          filters.auctionServer?.filter((item) => auctionServerId !== item)
        ),
    })) ?? [];

  const auctionTypeTags =
    filters.auctionType?.map((auctionType) => ({
      label: match(auctionType)
        .with(AuctionTypeFilter.BUY_NOW, () => i18n.t('entity.auction.labels.buyNow'))
        .with(AuctionTypeFilter.OPEN, () => i18n.t('entity.auction.labels.openAuction'))
        .with(AuctionTypeFilter.CLOSED, () => i18n.t('entity.auction.labels.closedAuction'))
        .otherwise(() => auctionType),
      onDelete: () =>
        onUpdateFilters(
          'auctionType',
          filters.auctionType?.filter((item) => auctionType !== item)
        ),
    })) ?? [];

  const carStatusTags =
    filters.carStatus?.map((carStatus) => ({
      label: match(carStatus)
        .with(CarStatusFilter.UPCOMING, () => i18n.t('entity.auction.labels.upcoming'))
        .with(CarStatusFilter.ACTIVE, () => i18n.t('general.labels.active'))
        .with(CarStatusFilter.ENDED, () => i18n.t('entity.auction.labels.ended'))
        .otherwise(() => carStatus),
      onDelete: () =>
        onUpdateFilters(
          'carStatus',
          filters.carStatus?.filter((item) => carStatus !== item)
        ),
    })) ?? [];

  const address = {
    street: filters.address?.street ?? '',
    descriptiveNumber: filters.address?.descriptiveNumber ?? null,
    orientationNumber: filters.address?.orientationNumber ?? null,
    city: filters.address?.city ?? '',
    zip: filters.address?.zipCode ?? '',
    addressComplement: null,
    country: filters.address?.country ?? '',
    prefix: filters.address?.prefix ?? null,
    district: null,
    state: filters.address?.state ?? null,
  };

  const locationTags =
    filters.address && filters.radius
      ? [
          {
            label: `${composeAddress(address)} (${filters.radius} km)`,
            onDelete: () => {
              onUpdateFilters('address', null);
              onUpdateFilters('radius', null);
            },
          },
        ]
      : [];

  const makeTags = selectedMakes.map((item) => ({
    label: getMake(item)?.label,
    onDelete: () => unselectMake(item),
  }));

  const modelFamilyTags = selectedModelFamilies.map((item) => ({
    label: getModelFamily(item)?.label,
    onDelete: () => unselectModelFamily(item),
  }));

  const modelTags = selectedModels.map((item) => ({
    label: getModel(item)?.label,
    onDelete: () => unselectModel(item),
  }));

  const adTitleTags = filters.adTitle
    ? [
        {
          label: filters.adTitle,
          onDelete: () => onUpdateFilters('adTitle', null),
        },
      ]
    : [];

  const featureTiersTags =
    filters.trimLevels?.map((trimLevel) => ({
      label: trimLevel,
      onDelete: () =>
        onUpdateFilters(
          'trimLevels',
          filters.trimLevels?.filter((item) => trimLevel !== item)
        ),
    })) ?? [];

  const colorTags =
    filters.selectedColors?.map((color) => ({
      label: color.label,
      onDelete: () =>
        onUpdateFilters(
          'selectedColors',
          filters.selectedColors?.filter((item) => color.key !== item.key)
        ),
    })) ?? [];

  const doorCountTags =
    filters.doorCount?.map((doorCount) => ({
      label: match(doorCount)
        .with('DOORCOUNT_1', () => i18n.t('entity.vehicle.labels.doors', {count: 1}))
        .with('DOORCOUNT_2_3', () => `2/${i18n.t('entity.vehicle.labels.doors', {count: 3})}`)
        .with('DOORCOUNT_4_5', () => `4/${i18n.t('entity.vehicle.labels.doors', {count: 5})}`)
        .with('DOORCOUNT_6_7', () => `6/${i18n.t('entity.vehicle.labels.doors', {count: 7})}`)
        .otherwise(
          () =>
            `${i18n.t('entity.vehicle.labels.numberOfDoors')}: ${getEnum('door_count').find(({key}) => key === doorCount)?.label}`
        ),
      onDelete: () =>
        onUpdateFilters(
          'doorCount',
          filters.doorCount?.filter((item) => item !== doorCount)
        ),
    })) ?? [];

  const tags = [
    ...getStringBasedTags('sellerCountry', 'country'),
    ...sellerServerTags,
    ...getStringBasedTags('sellerType', 'seller_type'),
    ...auctionTypeTags,
    ...carStatusTags,
    ...auctionServerTags,
    ...getNumberBasedTags(
      'avgNumberAds',
      i18n.t('entity.warehouse.labels.averageCarsInStock'),
      sourcingFilterConstants.AVG_NUMBER_ADS_MAX
    ),
    ...locationTags,
    ...getNumberBasedTags(
      'priceCZK',
      i18n.t('general.labels.price'),
      sourcingFilterConstants.PRICE_MAX
    ),
    ...(filters.isMarginAbsolute
      ? getNumberBasedTags(
          'margin',
          i18n.t('entity.vehicle.labels.margin'),
          sourcingFilterConstants.MARGIN_LIMIT_MAX,
          currencySuffix,
          {
            onDelete: () => {
              onUpdateFilters('margin', undefined);
              onUpdateFilters('marginPercent', undefined);
            },
          }
        )
      : getNumberBasedTags(
          'marginPercent',
          i18n.t('entity.vehicle.labels.margin'),
          sourcingFilterConstants.MARGIN_PERCENT_LIMIT_MAX,
          '%',
          {
            onDelete: () => {
              onUpdateFilters('margin', undefined);
              onUpdateFilters('marginPercent', undefined);
            },
          }
        )),
    ...getNumberBasedTags(
      'daysOnDisplay',
      i18n.t('entity.vehicle.labels.daysOnDisplay'),
      sourcingFilterConstants.DAYS_ON_DISPLAY
    ),
    ...getNumberBasedTags(
      'liquidity',
      i18n.t('entity.vehicle.labels.liquidity'),
      sourcingFilterConstants.LIQUIDITY_MAX
    ),
    ...getNumberBasedTags('pricePosition', i18n.t('entity.vehicle.labels.pricePosition'), 100, '%'),
    ...getBooleanBasedTags('reclaimableVat', i18n.t('general.labels.reclaimableVAT')),
    ...getBooleanBasedTags(
      'hideSuspicious',
      i18n.t('entity.sourcing.filters.labels.hideSuspicious')
    ),
    ...getBooleanBasedTags('hitlistOnly', i18n.t('entity.sourcing.filters.labels.hitlistOnly')),
    ...getBooleanBasedTags('hideBlacklist', i18n.t('entity.sourcing.filters.labels.hideBlacklist')),
    ...modelTags,
    ...modelFamilyTags,
    ...makeTags,
    ...getStringBasedTags('fuelType', 'fuel_type'),
    ...getStringBasedTags('transmission', 'transmission'),
    ...getStringBasedTags('bodyType', 'car_style'),
    ...doorCountTags,
    ...getStringBasedTags('drive', 'drive'),
    ...featureTiersTags,
    ...adTitleTags,
    ...getNumberBasedTags(
      'mileageRange',
      i18n.t('entity.vehicle.labels.mileageRange'),
      sourcingFilterConstants.MILEAGE_RANGE_MAX
    ),
    ...getNumberBasedTags('year', i18n.t('entity.vehicle.labels.year')),
    ...getNumberBasedTags(
      'power',
      i18n.t('entity.vehicle.labels.power2'),
      sourcingFilterConstants.POWER_LIMIT_MAX,
      filters.isPowerInKW ? ' kW' : ' PS',
      {
        formatter: (number) =>
          (filters.isPowerInKW ? number : (number ?? 0) * KwToPsMultiplier)?.toFixed(0) ?? null,
      }
    ),
    ...getNumberBasedTags(
      'cubicCapacity',
      i18n.t('entity.vehicle.labels.cubicCapacity'),
      sourcingFilterConstants.CUBIC_CAPACITY_LIMITS[1]
    ),
    ...getNumberBasedTags(
      'numberOfSeats',
      i18n.t('entity.vehicle.labels.numberOfSeats'),
      sourcingFilterConstants.NUMBER_OF_SEATS_MAX
    ),
    ...getNumberBasedTags(
      'featureScore',
      i18n.t('entity.vehicle.labels.rankEquipment'),
      sourcingFilterConstants.FEATURE_SCORE_MAX,
      '%'
    ),
    ...colorTags,
    ...getStringBasedTags('features', 'feature'),
  ];

  const mappedTags = tags.map(({label, onDelete}, index) => (
    <Tag
      key={label ? `tag-${label}-${index}` : `tag-${index}`}
      onDelete={onDelete}
      label={label ?? ''}
      colorScheme="blue"
      data-testid={suffixTestId(`tag-${label?.replace(' ', '_')}`, props)}
    />
  ));
  const isTagsLoading =
    props.filterPresetsControl.isLoading || areEnumsLoading || areMakeModelsLoading;

  return (
    <HStack width="100%" align="center" spacing={1} wrap>
      <SkeletonLoadingBox
        isLoading={isTagsLoading}
        width={22}
        height={6}
        borderRadius="small"
        contentStyles={{display: 'contents'}}
      >
        {tags.length <= NUMBER_OF_VISIBLE_TAGS || isExpanded
          ? mappedTags
          : slice(0, NUMBER_OF_VISIBLE_TAGS, mappedTags)}
      </SkeletonLoadingBox>
      <Space horizontal={1} />
      <FilterTagButtons
        tagCount={tags.length}
        collapsedTagCount={tags.length - NUMBER_OF_VISIBLE_TAGS}
        filterPresetsControl={props.filterPresetsControl}
        isExpanded={isExpanded}
        showSaveButton={props.showSaveButton}
        toggleIsExpanded={toggleIsExpanded}
        data-testid={suffixTestId('filterTagButtons', props)}
      />
    </HStack>
  );
}
